import React, { useEffect, useRef, useState } from 'react';
import Header from './Header';
import Footer from './Footer';
import CustomCursor from './CustomCursor';


function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Outfit:wght@300;400;500;600;700&display=swap');

  .icoffee-root {
    font-family: 'Outfit', sans-serif;
    --coffee:       #6B3A2A;
    --coffee-light: #A0522D;
    --coffee-dark:  #3B1A0E;
    --coffee-deep:  #1C0A04;
    --cream:        #D4A96A;
    --bg:           #FDF6EE;
    background: var(--bg);
  }
  .icoffee-root *, .icoffee-root *::before, .icoffee-root *::after { box-sizing: border-box; font-family: 'Outfit', sans-serif; }
  .icoffee-root h1, .icoffee-root h2, .icoffee-root h3,
  .icoffee-root h4, .icoffee-root h5, .icoffee-root h6 {
    font-family: 'Cormorant Garamond', serif;
  }

  /* ── HERO ── */
  .hero-section {
    position: relative; overflow: hidden;
    background: linear-gradient(135deg, #1C0A04 0%, #3B1A0E 50%, #2A0F06 100%);
    min-height: 88vh;
    display: flex; align-items: center; justify-content: center;
  }

  .hero-grid { position: absolute; inset: 0; pointer-events: none; perspective: 600px; }
  .hero-grid-inner {
    position: absolute; bottom: -40px; left: -20%; right: -20%; height: 60%;
    background-image:
      linear-gradient(rgba(160,82,45,0.12) 1px, transparent 1px),
      linear-gradient(90deg, rgba(160,82,45,0.12) 1px, transparent 1px);
    background-size: 60px 60px;
    transform: rotateX(70deg); transform-origin: bottom center;
    animation: grid-scroll 8s linear infinite;
    mask-image: linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 80%);
  }
  @keyframes grid-scroll { from { background-position: 0 0; } to { background-position: 0 60px; } }

  .hero-orbit {
    position: absolute; inset: -48px; border-radius: 50%;
    border: 1px solid rgba(160,82,45,0.3);
    animation: orbit3d 8s linear infinite; transform-style: preserve-3d;
  }
  .hero-orbit-2 { inset: -72px; border-color: rgba(212,169,106,0.18); animation: orbit3d 12s linear infinite reverse; }
  @keyframes orbit3d {
    0%   { transform: rotateX(70deg) rotateZ(0deg); }
    100% { transform: rotateX(70deg) rotateZ(360deg); }
  }
  .hero-orbit::after {
    content: ''; position: absolute; top: -4px; left: 50%;
    width: 8px; height: 8px; background: var(--coffee-light); border-radius: 50%;
    box-shadow: 0 0 12px 3px rgba(160,82,45,0.9); transform: translateX(-50%);
  }
  .hero-orbit-2::after { background: var(--cream); box-shadow: 0 0 12px 3px rgba(212,169,106,0.8); top: auto; bottom: -4px; }

  .hero-particle { position: absolute; border-radius: 50%; pointer-events: none; animation: particle-float linear infinite; }
  .hero-particle:nth-child(1) { width:4px; height:4px; background:rgba(160,82,45,0.8); left:15%; top:20%; animation-duration:7s; animation-delay:0s; }
  .hero-particle:nth-child(2) { width:3px; height:3px; background:rgba(212,169,106,0.7); left:80%; top:60%; animation-duration:9s; animation-delay:1s; }
  .hero-particle:nth-child(3) { width:5px; height:5px; background:rgba(160,82,45,0.5); left:60%; top:15%; animation-duration:11s; animation-delay:2s; }
  .hero-particle:nth-child(4) { width:3px; height:3px; background:rgba(255,220,180,0.4); left:30%; top:75%; animation-duration:8s; animation-delay:3s; }
  .hero-particle:nth-child(5) { width:4px; height:4px; background:rgba(212,169,106,0.6); left:90%; top:30%; animation-duration:10s; animation-delay:0.5s; }
  @keyframes particle-float {
    0%   { transform: translateY(0) scale(1); opacity: 0; }
    10%  { opacity: 1; } 90%  { opacity: 1; }
    100% { transform: translateY(-80px) scale(0.5); opacity: 0; }
  }

  .hero-blob { position: absolute; border-radius: 50%; filter: blur(80px); pointer-events: none; }
  .hero-blob-1 { width:500px; height:500px; background:rgba(107,58,42,0.4); top:-100px; right:-100px; animation:drift 10s ease-in-out infinite alternate; }
  .hero-blob-2 { width:360px; height:360px; background:rgba(160,82,45,0.25); bottom:-80px; left:-60px; animation:drift 13s ease-in-out infinite alternate-reverse; }
  @keyframes drift { from { transform:translate(0,0) scale(1); } to { transform:translate(30px,20px) scale(1.08); } }

  .hero-content {
    position: relative; z-index: 10;
    display: flex; flex-direction: row; align-items: center;
    gap: 80px; max-width: 1200px; width: 100%;
    padding: 120px 60px 80px 80px;
  }

  .hero-image-wrap { flex-shrink: 0; position: relative; }

  .hero-image-glow {
    position: absolute; inset: -24px;
    background: radial-gradient(circle, rgba(160,82,45,0.55) 0%, transparent 70%);
    border-radius: 50%; animation: glow-pulse 3s ease-in-out infinite;
  }
  @keyframes glow-pulse { 0%,100%{opacity:0.5;transform:scale(1);} 50%{opacity:1;transform:scale(1.06);} }

  .hero-image-3d {
    transform-style: preserve-3d; transition: transform 0.1s ease;
    cursor: pointer; position: relative;
  }
  .hero-image-3d::after {
    content: ''; position: absolute; inset: 0; border-radius: 24px;
    background: linear-gradient(135deg, rgba(255,255,255,0) 0%, rgba(255,220,180,0.08) 40%, rgba(255,220,180,0.2) 50%, rgba(255,220,180,0.08) 60%, rgba(255,255,255,0) 100%);
    background-size: 200% 200%; background-position: 200% 200%;
    z-index: 3; pointer-events: none; opacity: 0;
    transition: opacity 0.3s ease, background-position 0.4s ease;
  }
  .hero-image-3d:hover::after { opacity: 1; background-position: -20% -20%; }
  .hero-image-3d:hover .hero-image {
    box-shadow: 0 50px 100px rgba(0,0,0,0.7), 0 0 0 1px rgba(160,82,45,0.6),
      -20px 20px 60px rgba(107,58,42,0.5), 20px -10px 40px rgba(212,169,106,0.2);
  }
  .hero-image-3d::before {
    content: ''; position: absolute; inset: -2px; border-radius: 26px;
    background: linear-gradient(135deg, var(--coffee-light), var(--cream), var(--coffee));
    z-index: 1; opacity: 0; transition: opacity 0.3s ease;
  }
  .hero-image-3d:hover::before { opacity: 1; }

  /* ── UPDATED: larger square image ── */
  .hero-image {
    width: 380px; height: 380px; object-fit: cover; border-radius: 24px;
    position: relative; z-index: 2;
    box-shadow: 0 30px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(160,82,45,0.3), 20px 20px 60px rgba(0,0,0,0.3);
    transition: box-shadow 0.3s ease;
  }

  .hero-image-reflection {
    position: absolute; bottom: -20px; left: 10%; right: 10%; height: 30px;
    background: radial-gradient(ellipse, rgba(160,82,45,0.45) 0%, transparent 70%);
    filter: blur(8px); animation: reflection-pulse 3s ease-in-out infinite;
  }
  @keyframes reflection-pulse { 0%,100%{opacity:0.5;transform:scaleX(0.9);} 50%{opacity:0.9;transform:scaleX(1.05);} }

  .hero-text { flex: 1; padding-left: 40px; }

  .hero-eyebrow {
    font-family: 'Outfit', sans-serif;
    font-size: 11px; font-weight: 600; letter-spacing: 0.25em; text-transform: uppercase;
    color: var(--cream); margin-bottom: 16px;
    opacity: 0; animation: slideUp 0.8s ease forwards 0.3s;
  }

  .hero-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(54px,7vw,82px); font-weight: 600; line-height: 1;
    color: white; margin: 0 0 8px;
    opacity: 0; animation: slideUp 0.8s ease forwards 0.5s;
  }

  .hero-title-accent {
    display: block; font-family: 'Outfit', sans-serif;
    font-size: clamp(14px,1.8vw,18px); font-weight: 400;
    background: linear-gradient(90deg, var(--coffee-light), var(--cream));
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    background-clip: text; margin-top: 6px; letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .hero-divider {
    width: 60px; height: 2px;
    background: linear-gradient(90deg, var(--coffee-light), var(--cream));
    margin: 24px 0;
    opacity: 0; animation: slideUp 0.8s ease forwards 0.7s;
  }

  .hero-desc {
    font-family: 'Outfit', sans-serif;
    font-size: 17px; line-height: 1.78; color: rgba(255,255,255,0.7);
    max-width: 480px; font-weight: 300;
    opacity: 0; animation: slideUp 0.8s ease forwards 0.9s;
  }

  .back-btn-wrap {
    margin-top: 28px;
    opacity: 0; animation: slideUp 0.8s ease forwards 1.1s;
  }
  .back-products-btn {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 10px 22px; border-radius: 100px;
    background: rgba(255,255,255,0.07);
    border: 1px solid rgba(160,82,45,0.4);
    color: rgba(255,255,255,0.75);
    font-family: 'Outfit', sans-serif;
    font-size: 0.78rem; font-weight: 500;
    letter-spacing: 0.08em; text-transform: uppercase;
    text-decoration: none;
    transition: background 0.3s, border-color 0.3s, color 0.3s, transform 0.3s;
  }
  .back-products-btn:hover {
    background: rgba(160,82,45,0.18);
    border-color: rgba(160,82,45,0.7);
    color: var(--cream);
    transform: translateX(-4px);
  }

  .site-link-btn {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 10px 22px; border-radius: 100px;
    background: rgba(255,255,255,0.07);
    border: 1px solid rgba(212,169,106,0.45);
    color: var(--cream);
    font-family: 'Outfit', sans-serif;
    font-size: 0.78rem; font-weight: 500;
    letter-spacing: 0.08em; text-transform: uppercase;
    text-decoration: none;
    transition: background 0.3s, border-color 0.3s, color 0.3s, transform 0.3s;
  }
  .site-link-btn:hover {
    background: rgba(212,169,106,0.18);
    border-color: var(--cream);
    color: #fff;
    transform: translateX(-4px);
  }

  @keyframes slideUp { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }

  /* ── iCOFFEE IN ACTION ── */
  .action-section {
    background: linear-gradient(180deg, #0F0704 0%, #1C0A04 100%);
    padding: 100px 40px;
    position: relative; overflow: hidden;
  }
  .action-section::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(160,82,45,0.6), transparent);
  }
  .action-section::after {
    content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(212,169,106,0.3), transparent);
  }

  .action-inner {
    max-width: 1100px; margin: 0 auto;
    display: flex; flex-direction: row; align-items: center;
    gap: 80px;
  }

  .action-text { flex: 1; min-width: 0; }
  .action-eyebrow {
    font-family: 'Outfit', sans-serif; font-size: 0.6rem; font-weight: 500;
    letter-spacing: 0.3em; text-transform: uppercase; color: var(--cream);
    display: flex; align-items: center; gap: 12px; margin-bottom: 20px;
  }
  .action-eyebrow-line { display: inline-block; width: 32px; height: 1px; background: var(--coffee-light); }

  .action-heading {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(36px,5vw,60px); font-weight: 500; line-height: 1.1;
    color: white; margin: 0 0 8px;
  }
  .action-heading-italic {
    font-family: 'Cormorant Garamond', serif;
    font-style: italic; font-weight: 400;
    color: var(--cream); display: block; margin-top: 4px;
  }

  .action-divider {
    width: 48px; height: 1px;
    background: linear-gradient(90deg, var(--coffee-light), transparent);
    margin: 24px 0;
  }

  .action-desc {
    font-family: 'Outfit', sans-serif; font-weight: 300;
    font-size: 16px; line-height: 1.8; color: rgba(255,255,255,0.65);
    max-width: 420px; margin-bottom: 32px;
  }

  .action-yt-link {
    display: inline-flex; align-items: center; gap: 10px;
    padding: 12px 26px; border-radius: 100px;
    background: rgba(160,82,45,0.15);
    border: 1px solid rgba(160,82,45,0.45);
    color: var(--cream);
    font-family: 'Outfit', sans-serif; font-size: 0.78rem;
    font-weight: 500; letter-spacing: 0.08em; text-transform: uppercase;
    text-decoration: none;
    transition: background 0.3s, border-color 0.3s, transform 0.3s, box-shadow 0.3s;
  }
  .action-yt-link:hover {
    background: rgba(160,82,45,0.3);
    border-color: var(--cream);
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(107,58,42,0.4);
  }
  .action-yt-icon {
    width: 28px; height: 28px; background: #FF0000; border-radius: 6px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }

  .action-video-col { flex-shrink: 0; width: 460px; }

  .action-video-card {
    background: rgba(255,255,255,0.04); border: 1px solid rgba(160,82,45,0.28);
    border-radius: 16px; width: 100%; cursor: pointer;
    transition: background 0.3s, border-color 0.3s, box-shadow 0.3s, transform 0.3s;
    overflow: hidden;
  }
  .action-video-card:hover {
    background: rgba(160,82,45,0.12); border-color: rgba(160,82,45,0.6);
    transform: translateY(-6px) scale(1.02); box-shadow: 0 20px 48px rgba(107,58,42,0.35);
  }

  .action-video-thumb {
    position: relative; width: 100%; aspect-ratio: 16/9; overflow: hidden;
    background: rgba(107,58,42,0.3); display: flex; align-items: center; justify-content: center;
  }
  .action-video-thumb img {
    position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; opacity: 0.85;
    transition: transform 0.4s ease;
  }
  .action-video-card:hover .action-video-thumb img { transform: scale(1.06); }
  .action-play-overlay {
    position: absolute; inset: 0; background: rgba(28,10,4,0.45);
    display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 10px;
    transition: background 0.3s;
  }
  .action-video-card:hover .action-play-overlay { background: rgba(107,58,42,0.55); }
  .action-play-btn {
    width: 56px; height: 56px; background: rgba(255,255,255,0.95); border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 20px; padding-left: 4px; color: var(--coffee);
    box-shadow: 0 4px 20px rgba(107,58,42,0.5);
    transition: transform 0.3s, box-shadow 0.3s;
  }
  .action-video-card:hover .action-play-btn { transform: scale(1.15); box-shadow: 0 8px 30px rgba(107,58,42,0.7); }
  .action-yt-badge {
    background: #FF0000; color: white; font-family: 'Outfit', sans-serif;
    font-size: 9px; font-weight: 700; letter-spacing: 0.08em;
    padding: 3px 8px; border-radius: 4px; text-transform: uppercase;
  }
  .action-card-body { padding: 14px 18px 16px; text-align: left; }
  .action-card-name { font-family: 'Outfit', sans-serif; font-weight: 600; font-size: 15px; color: white; margin-bottom: 3px; }
  .action-card-topic { font-family: 'Outfit', sans-serif; font-size: 11px; color: rgba(212,169,106,0.85); font-weight: 500; letter-spacing: 0.04em; text-transform: uppercase; line-height: 1.4; }
  .action-card-watch { font-family: 'Outfit', sans-serif; font-size: 11px; color: rgba(255,255,255,0.4); margin-top: 5px; display: flex; align-items: center; gap: 4px; }

  /* ── LANGUAGE VIDEOS SECTION ── */
  .lang-videos-section {
    background: linear-gradient(180deg, #1C0A04 0%, #2E1208 100%);
    padding: 100px 40px; position: relative; overflow: hidden;
  }
  .lang-videos-section::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(90deg, transparent, var(--coffee-light), transparent);
  }

  .lang-videos-inner { max-width: 1100px; margin: 0 auto; text-align: center; }

  .section-label {
    font-family: 'Outfit', sans-serif;
    font-size: 11px; font-weight: 600; letter-spacing: 0.25em; text-transform: uppercase;
    color: rgba(212,169,106,0.85); margin-bottom: 12px;
  }
  .section-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(30px,4vw,44px); font-weight: 500;
    color: white; margin: 0 0 56px; line-height: 1.2;
  }

  .lang-video-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 20px; }
  @media (max-width: 900px) { .lang-video-grid { grid-template-columns: repeat(2,1fr); } }
  @media (max-width: 500px) { .lang-video-grid { grid-template-columns: 1fr; } }

  .lang-video-card {
    background: rgba(255,255,255,0.04); border: 1px solid rgba(160,82,45,0.28);
    border-radius: 16px; width: 100%; cursor: pointer;
    opacity: 0; transform: scale(0.92) translateY(20px);
    transition: opacity 0.6s ease, transform 0.6s ease, background 0.3s, border-color 0.3s, box-shadow 0.3s;
    overflow: hidden;
  }
  .lang-video-card.visible { opacity: 1; transform: scale(1) translateY(0); }
  .lang-video-card:hover {
    background: rgba(160,82,45,0.12); border-color: rgba(160,82,45,0.6);
    transform: translateY(-6px) scale(1.02); box-shadow: 0 20px 48px rgba(107,58,42,0.35);
  }

  .lang-video-thumb {
    position: relative; width: 100%; aspect-ratio: 16/9; overflow: hidden;
    background: rgba(107,58,42,0.3); display: flex; align-items: center; justify-content: center;
  }
  .lang-flag-bg { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font-size: 56px; opacity: 0.15; pointer-events: none; }
  .lang-play-overlay {
    position: absolute; inset: 0; background: rgba(28,10,4,0.45);
    display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 10px;
    transition: background 0.3s;
  }
  .lang-video-card:hover .lang-play-overlay { background: rgba(107,58,42,0.55); }
  .lang-play-btn {
    width: 52px; height: 52px; background: rgba(255,255,255,0.95); border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 18px; padding-left: 4px; color: var(--coffee);
    box-shadow: 0 4px 20px rgba(107,58,42,0.5);
    transition: transform 0.3s, box-shadow 0.3s; flex-shrink: 0;
  }
  .lang-video-card:hover .lang-play-btn { transform: scale(1.15); box-shadow: 0 8px 30px rgba(107,58,42,0.7); }
  .lang-yt-badge {
    background: #FF0000; color: white; font-family: 'Outfit', sans-serif;
    font-size: 9px; font-weight: 700; letter-spacing: 0.08em;
    padding: 3px 8px; border-radius: 4px; text-transform: uppercase;
  }

  .lang-card-body { padding: 12px 14px 14px; text-align: left; }
  .lang-name { font-family: 'Outfit', sans-serif; font-weight: 600; font-size: 14px; color: white; margin-bottom: 3px; }
  .lang-topic { font-family: 'Outfit', sans-serif; font-size: 11px; color: rgba(212,169,106,0.75); font-weight: 500; letter-spacing: 0.04em; text-transform: uppercase; line-height: 1.4; }
  .lang-watch { font-family: 'Outfit', sans-serif; font-size: 11px; color: rgba(255,255,255,0.35); margin-top: 5px; display: flex; align-items: center; gap: 4px; }

  /* ── VIDEO MODAL ── */
  .vid-modal-backdrop {
    position: fixed; inset: 0; background: rgba(10,4,2,0.9); z-index: 1000;
    display: flex; align-items: center; justify-content: center; padding: 24px;
    animation: modal-fade-in 0.25s ease;
  }
  @keyframes modal-fade-in { from{opacity:0;} to{opacity:1;} }
  .vid-modal-box {
    position: relative; width: 100%; max-width: 780px;
    background: #1C0A04; border-radius: 24px;
    border: 1px solid rgba(160,82,45,0.4);
    box-shadow: 0 40px 120px rgba(0,0,0,0.85), 0 0 0 1px rgba(160,82,45,0.2);
    overflow: hidden; animation: modal-slide-up 0.3s cubic-bezier(0.34,1.56,0.64,1);
  }
  @keyframes modal-slide-up { from{opacity:0;transform:scale(0.88) translateY(32px);} to{opacity:1;transform:scale(1) translateY(0);} }
  .vid-modal-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 18px 20px 14px; border-bottom: 1px solid rgba(160,82,45,0.18);
  }
  .vid-modal-title { font-family: 'Outfit', sans-serif; font-weight: 600; font-size: 15px; color: white; display: flex; align-items: center; gap: 8px; }
  .vid-modal-lang-badge {
    background: linear-gradient(135deg, var(--coffee), var(--coffee-light));
    color: white; font-family: 'Outfit', sans-serif; font-size: 10px; font-weight: 600;
    letter-spacing: 0.1em; text-transform: uppercase;
    padding: 3px 10px; border-radius: 100px;
  }
  .vid-modal-close {
    width: 32px; height: 32px; background: rgba(255,255,255,0.08);
    border: none; border-radius: 50%; color: rgba(255,255,255,0.7);
    font-size: 18px; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: background 0.2s, color 0.2s; flex-shrink: 0;
  }
  .vid-modal-close:hover { background: rgba(239,68,68,0.25); color: white; }
  .vid-modal-frame-wrap { position: relative; width: 100%; aspect-ratio: 16/9; background: #000; }
  .vid-modal-frame-wrap iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: none; }
  .vid-modal-footer { padding: 14px 20px; display: flex; align-items: center; justify-content: space-between; gap: 10px; }
  .vid-modal-yt-link {
    display: flex; align-items: center; gap: 6px;
    font-family: 'Outfit', sans-serif; font-size: 12px; font-weight: 600;
    color: rgba(255,255,255,0.5); text-decoration: none; transition: color 0.2s;
  }
  .vid-modal-yt-link:hover { color: #FF4444; }
  .vid-modal-topic { font-family: 'Outfit', sans-serif; font-size: 11px; color: rgba(212,169,106,0.7); letter-spacing: 0.06em; text-transform: uppercase; }

  @media (max-width: 900px) {
    .action-inner { flex-direction: column; gap: 48px; align-items: center; text-align: center; }
    .action-eyebrow { justify-content: center; }
    .action-desc { margin: 0 auto 32px; }
    .action-yt-link { margin: 0 auto; }
    .action-video-col { width: 100%; }
  }

  @media (max-width: 768px) {
    .hero-content { flex-direction: column; gap: 40px; padding: 100px 24px 60px; text-align: center; }
    .hero-text { padding-left: 0; }
    .hero-divider { margin: 24px auto; }
    .hero-desc { margin: 0 auto; }
  }
`;

const SHORTS_ID = 'c0iRRB1taJo';

const langVideos = [
  { lang: 'Hindi',    flag: '🇮🇳', youtubeId: 'OzpZ2pgEV3c', color: 'linear-gradient(135deg, rgba(251,146,60,0.35) 0%, rgba(120,53,15,0.6) 100%)',   accent: '#fb923c' },
  { lang: 'English',  flag: '🌐',  youtubeId: 'jGbftYskPtg', color: 'linear-gradient(135deg, rgba(160,82,45,0.45) 0%, rgba(60,20,10,0.6) 100%)',    accent: '#A0522D' },
  { lang: 'Tamil',    flag: '🇮🇳', youtubeId: 'Wj2D3lo3534', color: 'linear-gradient(135deg, rgba(234,179,8,0.35) 0%, rgba(113,63,18,0.6) 100%)',    accent: '#eab308' },
  { lang: 'Malayalam',flag: '🇮🇳', youtubeId: 'DORaJSqGX-8', color: 'linear-gradient(135deg, rgba(34,197,94,0.35) 0%, rgba(16,80,40,0.6) 100%)',    accent: '#22c55e' },
];

const langVideoStart = { 'Wj2D3lo3534': 0, 'DORaJSqGX-8': 0, 'OzpZ2pgEV3c': 0, 'jGbftYskPtg': 0 };

function AnimatedSection({ children }) {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} style={{ opacity: visible?1:0, transform: visible?'none':'translateY(28px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}>
      {children}
    </div>
  );
}

const iCoffee = () => {
  const langCardRefs = useRef(langVideos.map(() => React.createRef()));
  const [langCardsVisible, setLangCardsVisible] = useState(langVideos.map(() => false));
  const [activeVideo, setActiveVideo] = useState(null);
  const imageRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setActiveVideo(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleMouseMove = (e) => {
    const el = imageRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left, y = e.clientY - rect.top;
    const cx = rect.width / 2, cy = rect.height / 2;
    el.style.transform = `perspective(800px) rotateX(${-((y-cy)/cy)*18}deg) rotateY(${((x-cx)/cx)*18}deg) scale3d(1.06,1.06,1.06)`;
  };

  const handleMouseLeave = () => {
    if (imageRef.current) imageRef.current.style.transform = `perspective(800px) rotateX(0deg) rotateY(0deg) scale3d(1,1,1)`;
  };

  useEffect(() => {
    const obs = [];
    langCardRefs.current.forEach((ref, i) => {
      const o = new IntersectionObserver(([e]) => {
        if (e.isIntersecting) setLangCardsVisible(p => { const n=[...p]; n[i]=true; return n; });
      }, { threshold: 0.1 });
      if (ref.current) o.observe(ref.current);
      obs.push(o);
    });
    return () => obs.forEach(o => o.disconnect());
  }, []);

  return (
    <>
      <CustomCursor />
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <div className="icoffee-root min-h-screen flex flex-col">
        <Header />

        {/* ── Hero ── */}
        <section className="hero-section">
          <div className="hero-grid"><div className="hero-grid-inner" /></div>
          <div className="hero-particle" /><div className="hero-particle" /><div className="hero-particle" />
          <div className="hero-particle" /><div className="hero-particle" />
          <div className="hero-blob hero-blob-1" />
          <div className="hero-blob hero-blob-2" />

          <div className="hero-content">
            <div className="hero-image-wrap">
              <div className="hero-image-glow" />
              <div className="hero-orbit" />
              <div className="hero-orbit hero-orbit-2" />
              <div className="hero-image-3d" ref={imageRef} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
                <img src="/assest/product_icoffee.jpeg" alt="iCoffee" className="hero-image" />
              </div>
              <div className="hero-image-reflection" />
            </div>

            <div className="hero-text">
              <p className="hero-eyebrow">Functional Wellness Coffee</p>
              <h1 className="hero-title">
                iCoffee
                <span className="hero-title-accent">Product Description</span>
              </h1>
              <div className="hero-divider" />
              <p className="hero-desc">
                iCoffee is a functional coffee fortified with Salacia™ extract that helps manage pre-diabetes, regulates blood sugar levels, and supports metabolic balance. Experience a daily cup with purpose — combining traditional Ayurvedic wisdom with modern science for diabetes.
              </p>
              <div className="back-btn-wrap" style={{ display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap' }}>
                <a href="/products" className="back-products-btn">← Back to Products</a>
                <a href="https://vivaicoffee.com/" target="_blank" rel="noopener noreferrer" className="site-link-btn">
                  Visit iCoffee Site ↗
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ── iCoffee in Action ── */}
        <section className="action-section">
          <div className="action-inner">
            <AnimatedSection>
              <div className="action-text">
                <p className="action-eyebrow">
                  <span className="action-eyebrow-line" />
                  YouTube · Watch Now
                </p>
                <h2 className="action-heading">
                  iCoffee
                  <span className="action-heading-italic">in Action</span>
                </h2>
                <div className="action-divider" />
                <p className="action-desc">
                  Watch iCoffee in action — see how this science-backed functional coffee with Salacia™ extract fits seamlessly into your daily wellness routine and supports healthy blood sugar management.
                </p>
                <a href={`https://www.youtube.com/watch?v=${SHORTS_ID}`} target="_blank" rel="noopener noreferrer" className="action-yt-link">
                  <span className="action-yt-icon">
                    <svg width="14" height="10" viewBox="0 0 14 10" fill="white">
                      <path d="M5.5 7.5v-5l4.5 2.5-4.5 2.5z"/>
                    </svg>
                  </span>
                  Watch on YouTube
                </a>
              </div>
            </AnimatedSection>

            <AnimatedSection>
              <div className="action-video-col">
                <div className="action-video-card" onClick={() => setActiveVideo({ youtubeId: SHORTS_ID, lang: 'iCoffee in Action', topic: 'iCoffee · Wellness Coffee · Salacia™' })}>
                  <div className="action-video-thumb">
                    <img src={`https://i.ytimg.com/vi/${SHORTS_ID}/hqdefault.jpg`} alt="iCoffee in Action" onError={(e) => { e.target.style.display = 'none'; }} />
                    <div className="action-play-overlay">
                      <div className="action-play-btn">▶</div>
                      <span className="action-yt-badge">YouTube</span>
                    </div>
                  </div>
                  <div className="action-card-body">
                    <div className="action-card-name">iCoffee in Action</div>
                    <div className="action-card-topic">Diabetes Support · Sugar Balance · Salacia™</div>
                    <div className="action-card-watch"><span>▶</span> Watch Video</div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* ── Language Videos ── */}
        <section className="lang-videos-section">
          <div className="lang-videos-inner">
            <AnimatedSection>
              <p className="section-label">Watch &amp; Learn</p>
              <h2 className="section-title">iCoffee in Your Language</h2>
            </AnimatedSection>

            <div className="lang-video-grid">
              {langVideos.map((v, i) => (
                <div
                  key={i}
                  ref={langCardRefs.current[i]}
                  className={`lang-video-card ${langCardsVisible[i] ? 'visible' : ''}`}
                  style={{ transitionDelay: `${i * 110}ms` }}
                  onClick={() => setActiveVideo({ ...v, start: langVideoStart[v.youtubeId] || 0 })}
                >
                  <div className="lang-video-thumb" style={{ background: v.color }}>
                    <img
                      src={`https://i.ytimg.com/vi/${v.youtubeId}/hqdefault.jpg`}
                      alt={v.lang}
                      style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover', opacity:0.85 }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const fb = e.target.parentNode.querySelector('.lang-thumb-fallback');
                        if (fb) fb.style.display = 'flex';
                      }}
                    />
                    <div className="lang-thumb-fallback" style={{ display:'none', position:'absolute', inset:0, alignItems:'center', justifyContent:'center', flexDirection:'column', gap:'10px', background:v.color }}>
                      <div style={{ width:'52px', height:'52px', borderRadius:'50%', background:'rgba(255,255,255,0.12)', border:`2px solid ${v.accent}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'22px' }}>{v.flag}</div>
                      <span style={{ fontSize:'12px', fontWeight:700, color:v.accent, letterSpacing:'0.08em', textTransform:'uppercase' }}>{v.lang}</span>
                    </div>
                    <div className="lang-flag-bg">{v.flag}</div>
                    <div className="lang-play-overlay">
                      <div className="lang-play-btn">▶</div>
                      <span className="lang-yt-badge">YouTube</span>
                    </div>
                  </div>
                  <div className="lang-card-body">
                    <div className="lang-name">{v.lang}</div>
                    <div className="lang-topic">Diabetes Support · Sugar Balance</div>
                    <div className="lang-watch"><span>▶</span> Watch Video</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Unified Video Modal ── */}
        {activeVideo && (
          <div className="vid-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setActiveVideo(null); }}>
            <div className="vid-modal-box">
              <div className="vid-modal-header">
                <div className="vid-modal-title">
                  <span>iCoffee</span>
                  <span className="vid-modal-lang-badge">{activeVideo.lang}</span>
                </div>
                <button className="vid-modal-close" onClick={() => setActiveVideo(null)}>✕</button>
              </div>
              <div className="vid-modal-frame-wrap">
                <iframe
                  src={`https://www.youtube.com/embed/${activeVideo.youtubeId}?autoplay=1&rel=0&modestbranding=1${activeVideo.start ? `&start=${activeVideo.start}` : ''}`}
                  title={`iCoffee — ${activeVideo.lang}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="vid-modal-footer">
                <span className="vid-modal-topic">{activeVideo.topic || 'Diabetes Support · Sugar Balance · Ayurvedic'}</span>
                <a href={`https://www.youtube.com/watch?v=${activeVideo.youtubeId}`} target="_blank" rel="noopener noreferrer" className="vid-modal-yt-link">
                  <span style={{ fontSize:'14px' }}>▶</span> Open on YouTube
                </a>
              </div>
            </div>
          </div>
        )}
    {/* <TestimonyUpload/> */}
        <Footer />
      </div>
    </>
  );
};

export default iCoffee;